/**
 * bpob.ts — draw the BP channel's 023 order block (card `t_d894c573`). Compute nothing.
 *
 * Every number arrives from `/api/overlays/{a}/{tf}/bp_ob`, which is `ict.bp_ob` (lesson 023; the
 * rules R1-R6 and their timecodes live in that module). This file maps rows to shapes and decides
 * no rule — `overlays.ts`' contract: geometry in, pixels out.
 *
 *   zone     a box over the OB candle's high..low, wick to wick (R4), from the bar it became known
 *            on (`knowable_time`: the imbalance's third candle; a breaker's, the piercing gap's)
 *            to the bar that ended it (`end_time`), or the right edge while open. Nothing is drawn
 *            before it was known.
 *   kind     in the label: `regular` (opposite colour to the move), `engulfed` (same colour,
 *            023 05:55) or `breaker` (pierced by an imbalance, 023 06:59); a breaker is dashed.
 *   ended    a tested / pierced / closed-through zone keeps its box (audit evidence), grey, its
 *            label naming the kind and why it ended.
 *
 * Distinct from Dobrunia's `order_blocks` layer (`ict.order_blocks`, a different rule): `BP` on
 * the plate. OFF BY DEFAULT. NOT A SIGNAL: no entry, no stop, no size.
 */
import type { UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';

export type BpObKind = 'regular' | 'engulfed' | 'breaker';

export type BpObRow = {
  is_bull: boolean;
  kind: BpObKind;
  ob_idx: number;
  knowable_idx: number;
  lo: number;
  hi: number;
  end_idx: number | null;
  end_reason: 'tested' | 'breaker' | 'pierced' | 'closed_through' | null;
  time: UTCTimestamp;
  ob_time: UTCTimestamp;
  knowable_time: UTCTimestamp;
  end_time: UTCTimestamp | null;
};

export type BpObPayload = {
  component: 'bp_ob';
  source: string;
  counts: { total: number };
  blocks: BpObRow[];
};

export const BP_OB_STYLE = {
  bull: { fill: 'rgba(33, 150, 243, 0.14)', border: 'rgba(33, 150, 243, 0.80)' },
  bear: { fill: 'rgba(244, 81, 30, 0.13)', border: 'rgba(244, 81, 30, 0.80)' },
  ended: { fill: 'rgba(120, 120, 120, 0.06)', border: 'rgba(120, 120, 120, 0.35)' },
};

/** A shape that began before the pane's first bar starts at that bar (the `sessions.ts` clamp). */
const clamp = (t: UTCTimestamp, first: number | null): UTCTimestamp =>
  (first != null && t < first ? first : t) as UTCTimestamp;

export const bpObLabel = (r: BpObRow): string =>
  `BP ${r.kind === 'breaker' ? 'breaker' : `OB ${r.kind}`} ${r.is_bull ? '↑' : '↓'}`
  + (r.end_reason ? ` · ${r.end_reason.replace('_', ' ')}` : '');

/** One box per row; an ended zone whose end is left of the pane is not drawn. */
export function bpObBands(p: BpObPayload, windowFirstTime: number | null = null): ZoneBand[] {
  const out: ZoneBand[] = [];
  for (const r of p.blocks) {
    const open = r.end_reason == null;
    if (!open && windowFirstTime != null && (r.end_time as number) < windowFirstTime) continue;
    const style = open ? BP_OB_STYLE[r.is_bull ? 'bull' : 'bear'] : BP_OB_STYLE.ended;
    out.push({
      lo: r.lo,
      hi: r.hi,
      fromTime: clamp(r.knowable_time, windowFirstTime),
      toTime: open ? null : r.end_time,
      fill: style.fill,
      border: style.border,
      dashed: r.kind === 'breaker',
      label: bpObLabel(r),
      midline: null,
      exactSpan: true,
    });
  }
  return out;
}

export function bpObSummary(p: BpObPayload): [string, string][] {
  const open = p.blocks.filter((r) => r.end_reason == null);
  const n = (rows: BpObRow[], k: BpObKind) => rows.filter((r) => r.kind === k).length;
  return [
    ['BP OB (023)', `${p.blocks.length} in window: ${n(p.blocks, 'regular')} regular, ${n(p.blocks, 'engulfed')} engulfed, ${n(p.blocks, 'breaker')} breaker`],
    ['open', `${open.length} — the zone runs until a reaction, an imbalance pierce or a close through (023 06:59)`],
    ['what this is', 'closed bars only, the candle before a BP imbalance — NOT the order_blocks layer, NOT an entry signal'],
  ];
}

/** ONE CALL for every shape and the rows describing them (`bpImbalanceDrawing`'s shape). */
export function bpObDrawing(p: BpObPayload, windowFirstTime: number | null = null): {
  bands: ZoneBand[];
  rows: [string, string][];
} {
  return { bands: bpObBands(p, windowFirstTime), rows: bpObSummary(p) };
}
